import React, { useState, useEffect } from 'react';
import { Plus, Search, DollarSign, Save, Package, Eye, Edit, Trash2, BarChart2, Wallet, Table, Grid3X3, TrendingUp, TrendingDown, Minus, ChefHat, AlertTriangle, ClipboardList, Gavel, Store, X as XIcon, Boxes, RefreshCw, History, User, CalendarDays } from 'lucide-react';
import { api, Product, StockProduct, Ingredient, VirtualStockProduct } from '../services/api';
import { ProductViewModal } from './ProductViewModal';
import { ProductForm } from './ProductForm';
import toast from 'react-hot-toast';

export const Products: React.FC = () => {
  const [tab, setTab] = useState<'produtos' | 'virtual' | 'ingredientes'>('produtos');
  const [products, setProducts] = useState<Product[]>([]);
  const [stockData, setStockData] = useState<StockProduct[]>([]);
  const [virtualStock, setVirtualStock] = useState<VirtualStockProduct[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);
  const [deleteConfirmProduct, setDeleteConfirmProduct] = useState<Product | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showIngredientForm, setShowIngredientForm] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<Ingredient | null>(null);
  const [ingredientForm, setIngredientForm] = useState<Partial<Ingredient>>({ name: '', category: 'Vegetais', unit: 'kg', unitFamily: 'weight', quantityPerPackage: 0, stockQuantity: 0, minStockLevel: 1, costPrice: 0 });
  const [expandedRecipe, setExpandedRecipe] = useState<string | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [historyStartDate, setHistoryStartDate] = useState('');
  const [historyEndDate, setHistoryEndDate] = useState('');
  const [exportingCsv, setExportingCsv] = useState(false);

  const [formData, setFormData] = useState<Partial<Product>>({
    name: '', category: 'Geral', basePrice: 0, stockQuantity: 0, unit: 'un', images: [], shortDescription: '', sku: ''
  });

  const generateSKU = (name: string) => `${name.substring(0,3).toUpperCase()}-${Date.now().toString().slice(-4)}`;

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (tab === 'virtual') loadVirtualStock(); if (tab === 'ingredientes') loadIngredients(); }, [tab]);

  const loadData = async () => {
    try {
      const [productsData, stockOverview] = await Promise.all([
        api.products.getAll(),
        api.products.getStockOverview()
      ]);
      setProducts(productsData);
      setStockData(stockOverview);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    }
  };

    const loadHistory = async (search?: string, startDate?: string, endDate?: string) => {
    setLoadingHistory(true);
    try {
      const data = await api.stockHistory.list({ search, startDate, endDate, limit: 200 });
      setHistoryData(data);
    } catch { setHistoryData([]); }
    setLoadingHistory(false);
  };

  const handleHistorySearch = () => {
    loadHistory(historySearch || undefined, historyStartDate || undefined, historyEndDate || undefined);
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const blob = await api.stockHistory.exportCsv({
        search: historySearch || undefined,
        startDate: historyStartDate || undefined,
        endDate: historyEndDate || undefined
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `stock-history-${Date.now()}.csv`;
      a.click();
      toast.success('Relatório CSV exportado');
    } catch {
      toast.error('Erro ao exportar CSV');
    }
    setExportingCsv(false);
  };

  const loadVirtualStock = async () => {
    try {
      const data = await api.ingredients.getVirtualCalculations();
      setVirtualStock(data);
    } catch (error) {
      toast.error('Erro ao carregar stock virtual');
    }
  };


  const loadIngredients = async () => {
    try {
      const data = await api.ingredients.getAll();
      setIngredients(data);
    } catch (error) {
      toast.error('Erro ao carregar ingredientes');
    }
  };

  const stats = {
    totalRegistado: products.length,
    totalPublicado: products.filter(p => p.isActive).length,
    valorTotalStock: stockData.reduce((acc, p) => acc + (p.basePrice * p.stockQuantity), 0),
    lucroPotencialStock: stockData.reduce((acc, p) => acc + p.potentialProfit, 0),
    lucroRealizado: stockData.reduce((acc, p) => acc + p.totalProfit, 0),
  };

  const handleOpenForm = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setFormData(product);
    } else {
      setEditingProduct(null);
      setFormData({ name: '', category: 'Geral', basePrice: 0, stockQuantity: 0, unit: 'un', images: [], shortDescription: '', sku: '' });
    }
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...formData, sku: formData.sku || generateSKU(formData.name!) };
      if (editingProduct) {
        await api.products.update(editingProduct._id, payload);
        toast.success('Atualizado!');
      } else {
        await api.products.create(payload);
        toast.success('Criado!');
      }
      setShowForm(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleDeleteProduct = async (product: Product) => { setDeleteConfirmProduct(product); };
  const confirmDelete = async () => {
    if (!deleteConfirmProduct) return;
    try {
      setIsDeleting(true);
      await api.products.delete(deleteConfirmProduct._id);
      toast.success('Produto eliminado');
      setDeleteConfirmProduct(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar produto');
    } finally { setIsDeleting(false); }
  };

  const handleIngredientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingIngredient) {
        await api.ingredients.update(editingIngredient._id, ingredientForm);
        toast.success('Ingrediente actualizado!');
      } else {
        await api.ingredients.create(ingredientForm);
        toast.success('Ingrediente criado!');
      }
      setShowIngredientForm(false);
      setEditingIngredient(null);
      loadIngredients();
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao salvar ingrediente');
    }
  };

  const handleDeleteIngredient = async (id: string) => {
    if (!confirm('Tem a certeza que deseja eliminar este ingrediente?')) return;
    try {
      await api.ingredients.delete(id);
      toast.success('Ingrediente eliminado');
      loadIngredients();
    } catch (error) {
      toast.error('Erro ao eliminar ingrediente');
    }
  };

  const filteredStock = stockData.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStockStatus = (p: StockProduct) => {
    if (p.stockQuantity <= 0) return { label: 'Sem stock', color: 'bg-red-600 text-white', icon: Minus };
    if (p.stockQuantity <= p.minStockLevel) return { label: `Stock baixo: ${p.stockQuantity}`, color: 'bg-amber-100 text-amber-700', icon: TrendingDown };
    return { label: `${p.stockQuantity} em stock`, color: 'bg-green-100 text-green-700', icon: TrendingUp };
  };

  const ProfitIcon = ({ value }: { value: number }) => {
    if (value > 0) return <TrendingUp size={14} className="text-green-500" />;
    if (value < 0) return <TrendingDown size={14} className="text-red-500" />;
    return <Minus size={14} className="text-gray-400" />;
  };

  const tabs = [
    { id: 'produtos' as const, label: 'Produtos', icon: Package },
    { id: 'virtual' as const, label: 'Stock Virtual', icon: ChefHat },
    { id: 'ingredientes' as const, label: 'Ingredientes', icon: ClipboardList },
  ];

  // Virtual Stock card helper
  const VirtualStockCard = ({ v }: { v: VirtualStockProduct }) => {
    const isExpanded = expandedRecipe === v._id;
    const hasLowStock = v.maxServings <= 0;
    const isWarning = v.maxServings > 0 && v.maxServings <= 5;

    return (
      <div className={`bg-white rounded-xl border ${hasLowStock ? 'border-red-200 bg-red-50/30' : isWarning ? 'border-amber-200 bg-amber-50/20' : 'border-gray-100'} hover:shadow-md transition-all`}>
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <span className="text-[9px] font-bold text-orange-500 uppercase">Restaurante</span>
              <h4 className="font-bold text-gray-900 text-sm">{v.name}</h4>
            </div>
            <div className="text-right">
              <p className="font-black text-gray-900">{v.basePrice.toLocaleString('pt-MZ')} <span className="text-[9px] text-gray-400">MT</span></p>
              <p className="text-[10px] text-gray-500">Vendido: {v.totalSold}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <div className={`flex-1 h-2 rounded-full ${hasLowStock ? 'bg-red-100' : 'bg-green-100'}`}>
              <div className={`h-2 rounded-full transition-all ${hasLowStock ? 'bg-red-500 w-0' : v.maxServings <= 5 ? 'bg-amber-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(100, (v.maxServings / 20) * 100)}%` }} />
            </div>
            <span className={`text-xs font-bold ${hasLowStock ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-green-600'}`}>
              {v.maxServings} serv.
            </span>
          </div>

          {v.limitingIngredient && hasLowStock && (
            <div className="mt-2 flex items-center gap-1 text-[11px] text-red-600 font-bold bg-red-50 px-2 py-1 rounded-lg">
              <AlertTriangle size={12}/> Limitado por: {v.limitingIngredient}
            </div>
          )}

          <button onClick={() => setExpandedRecipe(isExpanded ? null : v._id)}
            className="mt-2 w-full text-[10px] font-bold text-gray-500 hover:text-gray-800 py-1 transition flex items-center justify-center gap-1">
            {isExpanded ? 'Ocultar receita' : 'Ver receita'} ({v.recipeIngredients.length} ingredientes)
          </button>

          {isExpanded && (
            <div className="mt-2 space-y-1 border-t border-gray-100 pt-2">
              {v.recipeIngredients.map((ri, idx) => (
                <div key={idx} className="flex items-center justify-between text-[11px] px-1">
                  <span className="text-gray-600">{ri.name}</span>
                  <span className={ri.availableStock < ri.neededPerUnit ? 'text-red-500 font-bold' : 'text-gray-500'}>
                    {ri.neededPerUnit}{ri.unit} / disp. {ri.availableStock}{ri.unit}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6">
      {/* Tab Navigation */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 mb-6 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${
              tab === t.id ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <t.icon size={16}/> {t.label}
          </button>
        ))}
      </div>

      {/* ============ PRODUTOS TAB ============ */}
      {tab === 'produtos' && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 rounded-xl"><Package className="w-5 h-5 text-blue-600" /></div>
              <div>
                <h2 className="text-xl font-black text-gray-900 uppercase tracking-tighter">Gestão de Stock</h2>
                <p className="text-xs text-gray-500 font-medium hidden sm:block">Produtos, quantidades, lucro por unidade</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-gray-100 rounded-xl p-1 border border-gray-200">
                <button onClick={() => setViewMode('cards')}
                  className={`p-2 rounded-lg transition ${viewMode === 'cards' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                  <Grid3X3 size={18} />
                </button>
                <button onClick={() => setViewMode('table')}
                  className={`p-2 rounded-lg transition ${viewMode === 'table' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                  <Table size={18} />
                </button>
              </div>
              <button onClick={() => handleOpenForm()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 font-bold shadow-lg shadow-blue-900/10 transition-all active:scale-95 text-sm">
                <Plus size={18} /> <span>Novo Produto</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <div className="bg-white p-4 rounded-xl border border-gray-100"><p className="text-xs text-gray-500 font-medium mb-1">Total Registado</p><p className="text-2xl font-black text-gray-800">{stats.totalRegistado}</p></div>
            <div className="bg-white p-4 rounded-xl border border-gray-100"><p className="text-xs text-gray-500 font-medium mb-1">Publicados</p><p className="text-2xl font-black text-green-600">{stats.totalPublicado}</p></div>
            <div className="bg-gray-900 p-4 rounded-xl"><p className="text-xs text-gray-400 font-medium mb-1">Valor em Stock</p><p className="text-2xl font-black text-white">{stats.valorTotalStock.toLocaleString('pt-MZ')} <span className="text-xs text-blue-400">MT</span></p></div>
            <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl"><p className="text-xs text-emerald-600 font-medium mb-1">Lucro Potencial</p><p className="text-2xl font-black text-emerald-700">{stats.lucroPotencialStock.toLocaleString('pt-MZ')} <span className="text-xs">MT</span></p></div>
            <div className="bg-green-600 p-4 rounded-xl"><p className="text-xs text-white/80 font-medium mb-1">Lucro Realizado</p><p className="text-2xl font-black text-white">{stats.lucroRealizado.toLocaleString('pt-MZ')} <span className="text-xs opacity-75">MT</span></p></div>
          </div>

          <div className="relative mb-4">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Buscar produto por nome ou SKU..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          {viewMode === 'cards' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredStock.map(p => {
                const status = getStockStatus(p);
                return (
                  <div key={p._id} onClick={() => { const prod = products.find(pr => pr._id === p._id); if (prod) setViewingProduct(prod); }}
                    className="bg-white rounded-xl border border-gray-100 hover:shadow-lg transition-all cursor-pointer group">
                    <div className="h-28 bg-gray-50 rounded-t-xl overflow-hidden relative">
                      {p.images?.[0] ? <img src={p.images[0]} className="w-full h-full object-cover" alt={p.name} /> :
                        <div className="w-full h-full flex items-center justify-center"><Package className="w-10 h-10 text-gray-300" /></div>}
                      <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${status.color}`}>
                        <status.icon size={12} className="inline mr-1" />{status.label}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="text-[9px] font-bold text-blue-500 uppercase">{p.category}</span>
                          <h4 className="font-bold text-gray-900 text-sm leading-tight">{p.name}</h4>
                          {p.sku && <p className="text-[10px] text-gray-400 font-mono">{p.sku}</p>}
                        </div>
                        <p className="font-black text-gray-900">{p.basePrice.toLocaleString('pt-MZ')} <span className="text-[9px] font-bold text-gray-400">MT</span></p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-50">
                        <div><p className="text-[9px] text-gray-400 uppercase font-bold">Stock</p><p className="text-sm font-bold text-gray-700">{p.stockQuantity} <span className="text-[9px] font-normal text-gray-400">{p.unit}</span></p></div>
                        <div><p className="text-[9px] text-gray-400 uppercase font-bold">Vendido</p><p className="text-sm font-bold text-gray-700">{p.totalSold} <span className="text-[9px] font-normal text-gray-400">{p.unit}</span></p></div>
                        <div><p className="text-[9px] text-gray-400 uppercase font-bold">Lucro/Un</p><p className={`text-sm font-bold flex items-center gap-1 ${p.profitPerUnit > 0 ? 'text-green-600' : p.profitPerUnit < 0 ? 'text-red-600' : 'text-gray-400'}`}><ProfitIcon value={p.profitPerUnit} />{p.profitPerUnit.toLocaleString('pt-MZ')} MT</p></div>
                        <div><p className="text-[9px] text-gray-400 uppercase font-bold">Lucro Total</p><p className={`text-sm font-bold ${p.totalProfit > 0 ? 'text-green-700' : 'text-gray-400'}`}>{p.totalProfit.toLocaleString('pt-MZ')} MT</p></div>
                      </div>
                      <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition">
                        <button onClick={(e) => { e.stopPropagation(); const prod = products.find(pr => pr._id === p._id); if (prod) handleOpenForm(prod); }}
                          className="flex-1 py-1.5 bg-gray-50 rounded-lg text-xs font-bold text-gray-600 hover:bg-amber-50 hover:text-amber-600 transition flex items-center justify-center gap-1"><Edit size={12} /> Editar</button>
                        <button onClick={(e) => { e.stopPropagation(); const prod = products.find(pr => pr._id === p._id); if (prod) handleDeleteProduct(prod); }}
                          className="flex-1 py-1.5 bg-gray-50 rounded-lg text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 transition flex items-center justify-center gap-1"><Trash2 size={12} /> Eliminar</button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredStock.length === 0 && (
                <div className="col-span-full text-center py-16 text-gray-500">
                  <Package className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                  <p className="font-bold">Nenhum produto encontrado</p>
                  <p className="text-sm">{searchTerm ? 'Tente outro termo de busca' : 'Clique em "Novo Produto" para começar'}</p>
                </div>
              )}
            </div>
          )}

          {viewMode === 'table' && (
            <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider">Produto</th>
                    <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider">SKU</th>
                    <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider">Categoria</th>
                    <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Preço</th>
                    <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Custo</th>
                    <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Lucro/Un</th>
                    <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Stock</th>
                    <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Vendido</th>
                    <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Lucro Total</th>
                    <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-center">Estado</th>
                    <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Acções</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredStock.map(p => {
                    const status = getStockStatus(p);
                    return (
                      <tr key={p._id} className="hover:bg-blue-50/30 transition cursor-pointer" onClick={() => { const prod = products.find(pr => pr._id === p._id); if (prod) setViewingProduct(prod); }}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                              {p.images?.[0] ? <img src={p.images[0]} className="w-full h-full object-cover" /> : <Package className="w-4 h-4 m-2 text-gray-400" />}
                            </div>
                            <span className="font-semibold text-gray-800">{p.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-400">{p.sku}</td>
                        <td className="px-4 py-3"><span className="text-[10px] font-bold bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{p.category}</span></td>
                        <td className="px-4 py-3 text-right font-semibold">{p.basePrice.toLocaleString('pt-MZ')}</td>
                        <td className="px-4 py-3 text-right text-gray-500">{p.costPrice > 0 ? p.costPrice.toLocaleString('pt-MZ') : '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-semibold flex items-center justify-end gap-1 ${p.profitPerUnit > 0 ? 'text-green-600' : p.profitPerUnit < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            <ProfitIcon value={p.profitPerUnit} />{p.profitPerUnit.toLocaleString('pt-MZ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">{p.stockQuantity}</td>
                        <td className="px-4 py-3 text-right font-semibold text-blue-600">{p.totalSold}</td>
                        <td className="px-4 py-3 text-right font-semibold text-green-700">{p.totalProfit.toLocaleString('pt-MZ')}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-[9px] font-bold px-2 py-1 rounded-full ${status.color}`}>{status.label}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={(e) => { e.stopPropagation(); const prod = products.find(pr => pr._id === p._id); if (prod) handleOpenForm(prod); }}
                              className="p-1.5 hover:bg-gray-100 rounded-lg transition"><Edit size={14} className="text-gray-500" /></button>
                            <button onClick={(e) => { e.stopPropagation(); if (p) handleDeleteProduct(p as any); }}
                              className="p-1.5 hover:bg-red-50 rounded-lg transition"><Trash2 size={14} className="text-gray-500 hover:text-red-500" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredStock.length === 0 && <tr><td colSpan={11} className="text-center py-12 text-gray-500">Nenhum produto encontrado</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ============ STOCK VIRTUAL TAB ============ */}
      {tab === 'virtual' && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-orange-50 rounded-xl"><ChefHat className="w-5 h-5 text-orange-600" /></div>
              <div>
                <h2 className="text-xl font-black text-gray-900 uppercase tracking-tighter">Stock Virtual</h2>
                <p className="text-xs text-gray-500 font-medium">Capacidade de produção baseada nos ingredientes disponíveis</p>
              </div>
            </div>
            <button onClick={loadVirtualStock} className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-200 transition">
              <RefreshCw size={14} /> Actualizar
            </button>
          </div>

          {virtualStock.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <ChefHat className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <p className="font-bold">Nenhum prato com receita</p>
              <p className="text-sm">Adicione ingredientes na receita de um produto Restaurante para ver o stock virtual</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {virtualStock.map(v => <VirtualStockCard key={v._id} v={v} />)}
            </div>
          )}
        </>
      )}

      {/* ============ INGREDIENTES TAB ============ */}
      {tab === 'ingredientes' && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-50 rounded-xl"><ClipboardList className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <h2 className="text-xl font-black text-gray-900 uppercase tracking-tighter">Armazém</h2>
                <p className="text-xs text-gray-500 font-medium">Gestão de ingredientes e matérias-primas</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { setShowHistoryModal(true); setHistorySearch(''); setHistoryStartDate(''); setHistoryEndDate(''); loadHistory(); }}
                className="border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl flex items-center gap-2 font-bold transition-all active:scale-95 text-sm">
                <History size={16} /> Histórico
              </button>
              <button onClick={() => { setEditingIngredient(null); setIngredientForm({ name: '', category: 'Vegetais', unit: 'kg', unitFamily: 'weight', quantityPerPackage: 0, stockQuantity: 0, minStockLevel: 1, costPrice: 0 }); setShowIngredientForm(true); }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 font-bold shadow-lg transition-all active:scale-95 text-sm">
                <Plus size={18} /> Novo Ingrediente
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase">Nome</th>
                  <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase">Categoria</th>
                  <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase">Unidade</th>
                  <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase text-right">Stock</th>
                  <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase text-right">Min. Seg.</th>
                  <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase text-right">Custo/Un</th>
                  <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase">Conservação</th>
                  <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase text-right">Acções</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {ingredients.map(ing => {
                  const isLow = ing.stockQuantity <= ing.minStockLevel;
                  return (
                    <tr key={ing._id} className={`hover:bg-gray-50 transition ${isLow ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-3 font-semibold text-gray-800">{ing.name}</td>
                      <td className="px-4 py-3"><span className="text-[10px] font-bold bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">{ing.category}</span></td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-400">{ing.unit}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${isLow ? 'text-red-600' : 'text-gray-800'}`}>
                          {ing.stockQuantity}
                          {isLow && <AlertTriangle size={12} className="inline ml-1 text-red-500" />}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500">{ing.minStockLevel}</td>
                      <td className="px-4 py-3 text-right">{ing.costPrice > 0 ? `${ing.costPrice.toLocaleString('pt-MZ')} MT` : '—'}</td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] font-medium text-gray-500">{ing.storageCondition || 'Ambiente'}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => { setEditingIngredient(ing); setIngredientForm(ing); setShowIngredientForm(true); }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition"><Edit size={14} className="text-gray-500" /></button>
                          <button onClick={() => handleDeleteIngredient(ing._id)}
                            className="p-1.5 hover:bg-red-50 rounded-lg transition"><Trash2 size={14} className="text-gray-500 hover:text-red-500" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {ingredients.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-gray-500">Nenhum ingrediente cadastrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Product Form Modal */}
      {showForm && <ProductForm formData={formData} setFormData={setFormData} onSubmit={handleSubmit} onClose={() => setShowForm(false)} isEditing={!!editingProduct} />}
      
      {/* Product View Modal */}
      {viewingProduct && <ProductViewModal product={viewingProduct} onClose={() => setViewingProduct(null)} />}

      {/* Delete Confirm */}
      {deleteConfirmProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
            <div className="p-6 border-b border-gray-200"><h2 className="text-lg font-black text-gray-900 uppercase">Confirmar Eliminação</h2></div>
            <div className="p-6 space-y-4">
              <p className="text-gray-600 font-medium">Tem a certeza que deseja eliminar o produto:</p>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="font-bold text-gray-900">{deleteConfirmProduct.name}</p>
                <p className="text-sm text-gray-500 font-mono">{deleteConfirmProduct.sku}</p>
              </div>
              <p className="text-sm text-red-600 font-medium">⚠️ Esta ação é irreversível.</p>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => setDeleteConfirmProduct(null)} disabled={isDeleting} className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-bold disabled:opacity-50">Cancelar</button>
              <button onClick={confirmDelete} disabled={isDeleting} className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                {isDeleting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Eliminando...</> : <><Trash2 size={16} /> Eliminar</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm" onClick={() => setShowHistoryModal(false)}>
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b flex justify-between items-center bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-600 rounded-lg text-white"><History size={20}/></div>
                <h3 className="text-xl font-bold text-gray-800">Histórico de Movimentações</h3>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="p-2 hover:bg-gray-200 rounded-full transition"><XIcon size={20}/></button>
            </div>

            {/* Filtros */}
            <div className="p-4 border-b bg-white flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={historySearch} onChange={e => setHistorySearch(e.target.value)}
                  placeholder="Buscar por item, tipo ou referência..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div className="flex items-center gap-2">
                <input type="date" value={historyStartDate} onChange={e => setHistoryStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
                <span className="text-gray-400 text-sm">a</span>
                <input type="date" value={historyEndDate} onChange={e => setHistoryEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <button onClick={handleHistorySearch}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-bold transition shadow flex items-center gap-1">
                <Search size={14}/> Filtrar
              </button>
              <button onClick={handleExportCsv} disabled={exportingCsv}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition shadow disabled:opacity-50 flex items-center gap-1">
                {exportingCsv ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <BarChart2 size={14}/>}
                Exportar CSV
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-16 text-gray-500 font-medium">Nenhuma movimentação encontrada</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr className="text-left">
                      <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase">Data</th>
                      <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase">Item</th>
                      <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase">Tipo</th>
                      <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase text-right">Antes</th>
                      <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase text-right">Δ</th>
                      <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase text-right">Depois</th>
                      <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase">Por</th>
                      <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase">Ref.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {historyData.map((h: any, i: number) => {
                      const delta = h.quantityChange ?? 0;
                      const isPos = delta > 0;
                      return (
                        <tr key={h._id || i} className="hover:bg-indigo-50/30 transition">
                          <td className="px-4 py-3 text-gray-500 font-mono text-xs whitespace-nowrap">{new Date(h.createdAt || h.date).toLocaleString('pt-MZ')}</td>
                          <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">{h.productName || h.ingredientName || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              ['sale', 'ingredient_deduction', 'product_sale'].includes(h.type) ? 'bg-red-50 text-red-600' :
                              ['ingredient_purchase', 'product_production', 'initial'].includes(h.type) ? 'bg-emerald-50 text-emerald-600' :
                              'bg-gray-100 text-gray-600'
                            }`}>{h.type || '—'}</span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 font-mono">{h.previousStock ?? '—'}</td>
                          <td className={`px-4 py-3 text-right font-mono font-bold ${isPos ? 'text-emerald-600' : 'text-red-600'}`}>
                            {isPos ? '+' : ''}{Number(delta).toLocaleString('pt-MZ', { minimumFractionDigits: 0 })}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-800 font-mono font-semibold">{h.newStock ?? '—'}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            <span className="flex items-center gap-1"><User size={10} />{h.userName || 'Sistema'}</span>
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-[10px] font-mono max-w-[100px] truncate" title={h.reference}>{h.reference || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-3 border-t bg-gray-50 text-center text-[11px] text-gray-400">
              {historyData.length} registos · {historySearch || historyStartDate ? 'filtro activo' : 'últimos 200 movimentos'}
            </div>
          </div>
        </div>
      )}

      {/* Ingredient Form Modal */}
      {showIngredientForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm" onClick={() => setShowIngredientForm(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b flex justify-between items-center bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-600 rounded-lg text-white"><ClipboardList size={20}/></div>
                <h3 className="text-xl font-bold text-gray-800">{editingIngredient ? 'Editar Ingrediente' : 'Novo Ingrediente'}</h3>
              </div>
              <button onClick={() => setShowIngredientForm(false)} className="p-2 hover:bg-gray-200 rounded-full transition"><XIcon size={20}/></button>
            </div>
            <form onSubmit={handleIngredientSubmit} className="p-6 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nome do Ingrediente *</label>
                <input required className="w-full p-2.5 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                  value={ingredientForm.name} onChange={e => setIngredientForm({...ingredientForm, name: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Categoria</label>
                  <select className="w-full p-2.5 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                    value={ingredientForm.category} onChange={e => setIngredientForm({...ingredientForm, category: e.target.value})}>
                    <option value="Vegetais">Vegetais</option>
                    <option value="Carnes">Carnes</option>
                    <option value="Peixes">Peixes</option>
                    <option value="Laticínios">Laticínios</option>
                    <option value="Grãos">Grãos</option>
                    <option value="Temperos">Temperos</option>
                    <option value="Óleos">Óleos</option>
                    <option value="Massas">Massas</option>
                    <option value="Frutas">Frutas</option>
                    <option value="Conservas">Conservas</option>
                    <option value="Outros">Outros</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Unidade</label>
                  <select className="w-full p-2.5 border rounded-xl outline-none"
                    value={ingredientForm.unit} onChange={e => setIngredientForm({...ingredientForm, unit: e.target.value})}>
                    <option value="kg">kg</option>
                    <option value="g">g</option>
                    <option value="L">L</option>
                    <option value="ml">ml</option>
                    <option value="un">unidade</option>
                    <option value="cx">caixa</option>
                    <option value="pc">pacote</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Família de Unidades</label>
                <select className="w-full p-2.5 border rounded-xl outline-none"
                  value={ingredientForm.unitFamily} onChange={e => setIngredientForm({...ingredientForm, unitFamily: e.target.value})}>
                  <option value="weight">Peso (kg, g)</option>
                  <option value="volume">Volume (L, ml)</option>
                  <option value="count">Unidade (un, cx, pc)</option>
                  <option value="length">Comprimento (m, cm)</option>
                  <option value="other">Outro</option>
                </select>
                <p className="text-[10px] text-gray-400 mt-1">Define como o sistema converte unidades (ex: 500ml → 0.5L)</p>
              </div>
              {(ingredientForm.unitFamily === 'count' || ['cx', 'pct', 'pc', 'pacote', 'caixa', 'emb', 'pack'].includes(ingredientForm.unit || '')) && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Unidades por Embalagem</label>
                  <input type="number" min="0" className="w-full p-2.5 border rounded-xl outline-none"
                    value={ingredientForm.quantityPerPackage || 0} onChange={e => setIngredientForm({...ingredientForm, quantityPerPackage: Number(e.target.value)})} />
                  <p className="text-[10px] text-gray-400 mt-1">Quantas unidades existem dentro de cada caixa/pacote</p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Stock Actual</label>
                  <input type="number" min="0" step="0.1" className="w-full p-2.5 border rounded-xl outline-none"
                    value={ingredientForm.stockQuantity} onChange={e => setIngredientForm({...ingredientForm, stockQuantity: Number(e.target.value)})} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Stock Mínimo</label>
                  <input type="number" min="0" className="w-full p-2.5 border rounded-xl outline-none"
                    value={ingredientForm.minStockLevel} onChange={e => setIngredientForm({...ingredientForm, minStockLevel: Number(e.target.value)})} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Custo/Un (MT)</label>
                  <input type="number" min="0" className="w-full p-2.5 border rounded-xl outline-none"
                    value={ingredientForm.costPrice} onChange={e => setIngredientForm({...ingredientForm, costPrice: Number(e.target.value)})} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Conservação</label>
                <select className="w-full p-2.5 border rounded-xl outline-none"
                  value={ingredientForm.storageCondition} onChange={e => setIngredientForm({...ingredientForm, storageCondition: e.target.value})}>
                  <option value="Ambiente">Ambiente</option>
                  <option value="Fresco">Fresco</option>
                  <option value="Refrigerado">Refrigerado</option>
                  <option value="Congelado">Congelado</option>
                </select>
              </div>
              <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-bold transition-all active:scale-[0.98] flex items-center justify-center gap-2">
                <Save size={18}/> {editingIngredient ? 'Salvar Alterações' : 'Adicionar Ingrediente'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
